import type { HTMLAttributes, ReactNode } from 'react';

// `Omit<..., 'title'>` : `HTMLAttributes` déclare déjà `title?: string` (l'attribut
// HTML natif, infobulle) — incompatible avec le `title` ReactNode ci-dessous
// (propriété PROPRE à ce composant, jamais rendue comme attribut `title=`
// natif sur le `<div>`, voir `Panel()` : consommée puis exclue de `...rest`).
export interface PanelProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  // UX-21 : élargi de `string` à `ReactNode` — permet d'embarquer un emoji
  // décoratif (`aria-hidden`) à côté du texte du titre sans dupliquer le
  // balisage du titre dans chaque composant appelant.
  title?: ReactNode;
  /**
   * Niveau de titre rendu pour `title` (UX-74, même patron que `titleAs` sur
   * `Card` — voir Card.tsx). `Panel` rendait systématiquement un `<h4>`, sans
   * jamais tenir compte du niveau de titre réel de la page englobante — sur
   * une page dont le titre est un `<h1>` (`Card` par défaut depuis UX-72),
   * ça produisait un saut `h1` → `h4`.
   *
   * Défaut `h2` : la quasi-totalité des `Panel` titrés du dépôt sont des
   * sections de premier niveau à l'intérieur d'une page dont le titre est un
   * `<h1>` (`Card title` ou en-tête personnalisé, voir CLAUDE.md/UX-72) —
   * `h2` est donc le niveau correct SANS modification pour ces appelants
   * (même raisonnement que le défaut `h1` de `Card` : corriger le défaut
   * plutôt que d'imposer une correction appelant par appelant). Les
   * `Panel` imbriqués plus profondément (tiroir/dialog avec son propre
   * `<h2>`, section elle-même sous un `<h2>`, panneau niché dans un autre
   * `Panel`...) passent `titleAs="h3"` explicitement.
   */
  titleAs?: 'h2' | 'h3' | 'h4';
  children: ReactNode;
}

// UX-03 : introduit par ce ticket — regroupement visuel des SECTIONS DE CONTENU
// à l'intérieur d'une page déjà enveloppée par `Card` (voir Card.tsx) : liste,
// formulaire, récapitulatif de confirmation, bannière de doublon... Avant ce
// ticket, ces sections n'avaient AUCUNE bordure (`<section className="flex
// flex-col gap-N">` nu, voir l'audit complet dans docs/architecture.md, section
// "UX-03") — la hiérarchie visuelle entre "la page" et "une section de contenu
// à l'intérieur de la page" n'était donc perceptible qu'à travers l'espacement,
// pas une vraie limite visuelle.
//
// Bordure/rayon DÉLIBÉRÉMENT différents de `Card` — pas la simple répétition
// du même style à une échelle imbriquée :
// - Même COULEUR de bordure que `Card` (`border-border`, socle cohérent),
//   même LARGEUR (`border`, 1px).
// - `rounded-lg` (pas `rounded-xl` comme `Card`) : un rayon légèrement plus
//   serré pour l'élément imbriqué évite qu'il "rivalise" visuellement avec le
//   coin de la `Card` qui l'entoure — hiérarchie perceptible même quand les
//   deux bordures sont proches l'une de l'autre (padding de `Card` réduit).
// - AUCUNE ombre (contrairement à `Card`, `shadow-sm`) : plusieurs `Panel`
//   empilés ou juxtaposés dans une page produiraient une accumulation
//   d'ombres ("shadow soup") sans valeur ajoutée — la bordure seule suffit à
//   distinguer un panneau, l'ombre reste réservée au conteneur de PREMIER
//   niveau de la page.
// - Padding plus léger (`p-4` vs `p-8` de `Card`) : les panneaux sont
//   généralement plusieurs par page (voir l'audit), un padding aussi généreux
//   que `Card` gonflerait excessivement la hauteur totale de la page.
//
// Fonctionne SANS adaptation dans une grille (UX-04, `grid`/`flex` de
// plusieurs `Panel` côte à côte) : `w-full` seul, aucune largeur maximale
// imposée par le composant — même raisonnement que `Card` `size="page"`, voir
// Card.tsx.
export function Panel({ title, titleAs: TitleTag = 'h2', className, children, ...rest }: PanelProps) {
  const classes = ['ss-panel', 'w-full rounded-lg border border-border bg-white p-4', className].filter(Boolean).join(' ');
  return (
    <div className={classes} {...rest}>
      {title ? <TitleTag className="ss-panel__title mb-3 text-sm font-semibold text-text">{title}</TitleTag> : null}
      {children}
    </div>
  );
}
